import React, { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { Patient } from "@models/patient";
import { useUpdatePatientMutation } from "@store/patientApi";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FamilyMemberFormType,
  PatientFormType,
  patientToFormType,
} from "@utils/patientFormHelper";
import {
  OnboardingFormActionContainer,
  OnboardingFormContainer,
} from "@components/Onboarding/OnboardingForm";
import { setMessage } from "@store/snackbarSlice";
import FamilyInfoForm from "@components/FamilyInfoForm/FamilyInfoForm";
import { withOnboardingFlow } from "@components/Onboarding/onboardingFlow";

const OnboardingFamilyInfoForm: FC<{ patient: Patient }> = ({ patient }) => {
  const { t } = useTranslation();
  const [updatePatient] = useUpdatePatientMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const form = useForm<PatientFormType>({
    mode: "onChange",
    criteriaMode: "all",
  });

  const {
    reset,
    formState: { isSubmitting },
    getValues,
    clearErrors,
    handleSubmit,
  } = form;

  useEffect(() => {
    if (patient) {
      reset(patientToFormType(patient));
    }
  }, [patient, reset]);

  const save = async (navigateNext = true) => {
    const values = getValues();
    try {
      const { data, error } = await updatePatient({
        ...values,
        id: patient?.id,
      } as unknown as Patient);
      if (data && !error) {
        if (navigateNext) {
          navigate(
            withOnboardingFlow(`/onboarding/${patient.id}/pictures`, location.search),
          );
        }
      } else {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
  };

  const onAdd = useCallback(() => {
    navigate("add");
  }, [navigate]);

  const onEdit = useCallback(
    ({ id }: FamilyMemberFormType) => {
      navigate(withOnboardingFlow(`/onboarding/family-info/${id!}`, location.search));
    },
    [navigate, location.search],
  );

  const onPrevious = useCallback(() => {
    const previousUrl = patient
      ? `/onboarding/${patient.id}/profile`
      : "/onboarding/profile";
    navigate(withOnboardingFlow(previousUrl, location.search));
  }, [navigate, patient, location.search]);

  return (
    <OnboardingFormContainer
      onSubmit={(e) => {
        clearErrors();
        return handleSubmit(() => save(true))(e);
      }}
    >
      <FamilyInfoForm
        form={form}
        onEdit={onEdit}
        onAdd={onAdd}
        onSave={() => save(false)}
        characters={[]}
      />

      <OnboardingFormActionContainer
        isSubmitting={isSubmitting}
        onPrevious={onPrevious}
      />
    </OnboardingFormContainer>
  );
};

export default OnboardingFamilyInfoForm;
